import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox } from 'react-bootstrap';
import { translate } from 'react-i18next';
import { isEmpty } from 'lodash/lang';
import { trim } from 'lodash/string';
import IntlTelInput from 'react-intl-tel-input';
import FinnishSSN from 'finnish-ssn';
import Select from 'react-select';

import ErrorMessage from '../../components/Messages/ErrorMessage';
import i18n from '../../i18n';
import InnerHtmlContent from '../../components/InnerHtmlContent';
import { cyrillicLetterScanner } from '../../util/helpers';
import api from '../../api';

// Custom styles used by react-select 
const customStyles = {
  control: base => ({
    ...base,
    height: 42,
    minHeight: 42,
    marginTop: '10px',
    borderRadius: 0,
    paddingLeft: '6px'
  }),
  menu: base => ({
    ...base,
    zIndex: 2
  })
};

class RegistrationForm extends Component {

  constructor(props) {
    super(props);

    this.timeoutPhoneInputChange = null;

    const sotuIsValid = FinnishSSN.validate(this.props.personId);
    const withoutSotu = (props.relaxedSotuVerificationEnabled && !sotuIsValid);

    this.state = {
      allFieldsValid: false,
      address: '',
      postcode: '',
      city: '',
      phone: '',
      email: '',
      municipality: '',
      acceptedTerms: false,
      language: i18n.language,
      addressValid: false,
      postcodeValid: false,
      cityValid: false,
      phoneValid: false,
      emailValid: false,
      acceptedTermsValid: false,
      municipalityValid: false,
      validationError: '',
      cyrillicLettersDetected: false,
      exclusiveData: {},
      exclusiveDataValid: [],
      sotuValid: sotuIsValid,
      municipalities: [],
      registrationExclusiveFieldsFiltered: props.registrationExclusiveFields.filter(field => field.registrationCase === 'ALWAYS' ||
                                                                              (withoutSotu && field.registrationCase === 'WITHOUT_SOTU') ||
                                                                              (!withoutSotu && field.registrationCase === 'WITH_SOTU'))
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handlePhoneInputChange = this.handlePhoneInputChange.bind(this);
    this.handleExclusiveFieldChange = this.handleExclusiveFieldChange.bind(this);
  }

  componentDidMount() {
    api.getMunicipalities().then((municipalities) => {
      const municipalityList = [];
      municipalities.map(item => municipalityList.push({ label: item, value: item }));
      this.setState({ municipalities: municipalityList });
    });
  }

  validateFields() {
    const POSTAL_CODE_REGEX = /^\d{5}$/;
    const sotuValid = FinnishSSN.validate(this.props.personId);
    const { address, postcode, city, phone, acceptedTerms, email, municipality } = this.state;
    const addressValid = !cyrillicLetterScanner(address) && (!sotuValid || (address.length > 0));
    const postcodeValid = !sotuValid || postcode.match(POSTAL_CODE_REGEX);
    const cityValid = (city.length > 0) && !cyrillicLetterScanner(city);
    const emailValid = (sotuValid && !cyrillicLetterScanner(email) && (email.length === 0 || this.isEmailValid(email))) || (!sotuValid && this.isEmailValid(email));
    const acceptedTermsValid = acceptedTerms === true;
    const phoneValid = this.isPhoneValid(phone);
    const cyrillicLettersDetected = cyrillicLetterScanner(address) || cyrillicLetterScanner(city) || cyrillicLetterScanner(email);
    const municipalityValid = (municipality.length > 0);

    const allFieldsValid = !cyrillicLettersDetected && (addressValid && postcodeValid && cityValid && phoneValid && emailValid && acceptedTermsValid && municipalityValid);
    this.setState({
      addressValid,
      postcodeValid,
      cityValid,
      phoneValid,
      emailValid,
      acceptedTermsValid,
      allFieldsValid,
      validationError: '',
      cyrillicLettersDetected,
      municipalityValid
    });
  }

  isPhoneValid(phone) {
    const telUtils = window.intlTelInputUtils;
    return telUtils.isValidNumber(phone) &&
            (telUtils.getNumberType(phone) === telUtils.numberType.MOBILE ||
              telUtils.getNumberType(phone) === telUtils.numberType.FIXED_LINE_OR_MOBILE);
  }

  isEmailValid(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  handlePhoneInputChange(status, inputVal, countryData, number) {
    const phone = number.replace(/\s/g, '');
    this.setState({ phone }, this.validateFields);
  }

  handleChange(event) {
    const { id: fieldName, value } = event.target;
    this.setState({ [fieldName]: trim(value) }, this.validateFields);
  }

  handleExclusiveFieldChange(event, index, validationPattern) {
    const { id: fieldName, value } = event.target;

    let valid = true;
    if (validationPattern) {
      valid = (value.match(validationPattern) != null);
    }
    this.state.exclusiveDataValid[index] = valid;

    const newExclusiveData = { ...this.state.exclusiveData, [fieldName]: value };
    this.setState({ exclusiveData: newExclusiveData });
  }

  handleCheckboxChange(event) {
    this.setState({ acceptedTerms: event.target.checked }, this.validateFields);
  }

  handleSubmit(event) {
    event.preventDefault();

    const allExclusiveFieldsValid = this.state.exclusiveDataValid.every(isValid => isValid === true);

    if (this.state.allFieldsValid && allExclusiveFieldsValid) {
      const { address, postcode, city, phone, acceptedTerms, language, email, municipality, exclusiveData } = this.state;
      const { firstName, lastName, personId } = this.props;

      if (this.props.creationIncomplete) {
        this.props.updateUser({ address, postcode, city, phone, acceptedTerms, email, municipality },
          this.props.afterSuccess);
      } else {
        this.props.registerUser({ personId, firstName, lastName, address, postcode, city, phone, acceptedTerms, language, email, municipality, exclusiveData },
          this.props.afterSuccess);
      }
    } else if (this.state.cyrillicLettersDetected) {
      this.setState({ validationError: 'cyrillicLettersNotAllowed' });
    } else if (!this.state.addressValid) {
      this.setState({ validationError: 'addressMissing' });
    } else if (!this.state.postcodeValid) {
      this.setState({ validationError: 'postcodeMissing' });
    } else if (!this.state.cityValid) {
      this.setState({ validationError: 'cityMissing' });
    } else if (!this.state.phoneValid) {
      this.setState({ validationError: 'invalidPhone' });
    } else if (!this.state.emailValid) {
      this.setState({ validationError: 'invalidEmail' });
    } else if (!this.state.acceptedTermsValid) {
      this.setState({ validationError: 'invalidAcceptedTerms' });
    } else if (!this.state.municipalityValid) {
      this.setState({ validationError: 'invalidMunicipality' });
    } else if (!allExclusiveFieldsValid) {
      this.setState({ validationError: 'validation.pattern' });
    }
  }

  render() {
    if (this.props.userIdentified && !this.props.creationIncomplete) {
      return null;
    }

    const { registrationFailed, registrationFailedMessage,
            termsOfServiceUrl, t } = this.props;

    const registrationExclusiveFieldsItems = this.state.registrationExclusiveFieldsFiltered.map((field, index) =>
      <div className="form-input-group" key={field.ehrProfileProperty}>
        <input
          id={field.ehrProfileProperty}
          className={this.state.exclusiveDataValid[index] ? 'is-valid' : ''}
          type="text"
          placeholder={t(`identification:registrationForm.exclusiveFields.${field.localizationKey}`)}
          onChange={e => this.handleExclusiveFieldChange(e, index, field.validation)}
        />
      </div>
    );

    return (
      <form className="registration-form" onSubmit={e => this.handleSubmit(e)}>
        <div className="form-input-group">
          <input
            id="address"
            className={this.state.addressValid ? 'is-valid' : ''}
            type="text"
            placeholder={t('identification:registrationForm.addressPlaceholder')}
            onChange={this.handleChange}
          />
        </div>
        <div className="form-input-group">
          <input
            id="postcode"
            className={this.state.postcodeValid ? 'is-valid' : ''}
            type="text"
            placeholder={t('identification:registrationForm.postcodePlaceholder')}
            onChange={this.handleChange}
          />
        </div>
        <div className="form-input-group">
          <input
            id="city"
            className={this.state.cityValid ? 'is-valid' : ''}
            type="text"
            placeholder={t('identification:registrationForm.cityPlaceholder')}
            onChange={this.handleChange}
          />
        </div>
        <Select
          id="municipality"
          className={this.state.municipalityValid ? 'municipality is-valid' : 'municipality'}
          options={this.state.municipalities}
          value={this.state.municipalities.filter(({ value }) => value === this.state.municipality)}
          onChange={value => this.handleChange({ target: { id: 'municipality', value: value.value } })}
          placeholder={t('identification:registrationForm.municipalityPlaceholder')}
          styles={customStyles}
        />
        <div className="form-input-group form-input-group-phone">
          <IntlTelInput
            fieldId={'visiblePhone'}
            numberType={'MOBILE'}
            preferredCountries={['fi', 'se', 'ee']}
            style={{ width: '100%' }}
            onPhoneNumberChange={this.handlePhoneInputChange}
            useMobileFullscreenDropdown={false}
            placeholder={t('identification:registrationForm.phoneNumberPlaceholder')}
            inputClassName={`form-control ${this.state.isPhoneValid ? 'is-valid' : ''}`}
          />
        </div>
        <div className="form-input-group">
          <input
            id="email"
            className={this.state.emailValid ? 'is-valid' : ''}
            type="text"
            placeholder={t('identification:registrationForm.emailPlaceholder')}
            onChange={this.handleChange}
          />
        </div>
        {registrationExclusiveFieldsItems}
        <div>
          <Checkbox
            id="acceptedTerms"
            className={this.state.acceptedTermsValid ? 'is-valid' : ''}
            onChange={this.handleCheckboxChange}
          >
            <div className="terms-label-container">
              <div className="terms-label"><InnerHtmlContent content={t('identification:registrationForm.termsOfService')} /></div>
              <div className="terms-link"><a href={termsOfServiceUrl} target={'_blank'}><InnerHtmlContent content={t('identification:registrationForm.termsOfServiceLink')} /></a></div>
            </div>
          </Checkbox>
        </div>
        <ErrorMessage isVisible={!isEmpty(this.state.validationError) || this.state.cyrillicLettersDetected}>
          <strong>{this.state.cyrillicLettersDetected ? t('cyrillicLettersNotAllowed') : t(this.state.validationError)}</strong>
        </ErrorMessage>
        <ErrorMessage isVisible={registrationFailed}>
          <strong>{t('registrationFailedTitle')}</strong>
          { registrationFailedMessage &&
            <p>{t(registrationFailedMessage)}</p>
          }
        </ErrorMessage>
        <Button
          className="action-btn center-block login-button"
          type="submit"
        >
          {t('identification:registrationForm.continueButtonText')}
        </Button>
      </form>
    );
  }
}

RegistrationForm.propTypes = {
  registerUser: PropTypes.func.isRequired,
  userIdentified: PropTypes.bool.isRequired,
  afterSuccess: PropTypes.func,
  registrationFailed: PropTypes.bool.isRequired,
  registrationFailedMessage: PropTypes.string,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  personId: PropTypes.string.isRequired,
  termsOfServiceUrl: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  registrationExclusiveFields: PropTypes.array,
  relaxedSotuVerificationEnabled: PropTypes.bool.isRequired,
  creationIncomplete: PropTypes.bool.isRequired,
  updateUser: PropTypes.func.isRequired
};

RegistrationForm.defaultProps = {
  afterSuccess: null,
  registrationFailedMessage: null,
  firstName: '',
  lastName: '',
  personId: '',
  termsOfServiceUrl: '',
  registrationExclusiveFields: []
};

export default translate(['common', 'identification'], { wait: true })(RegistrationForm);



// WEBPACK FOOTER //
// ./src/containers/RegistrationForm/RegistrationForm.jsx